import { __awaiter } from '../../../_virtual/_tslib.js';
import { createPublicClient, http, formatEther, custom, createWalletClient } from 'viem';
import { createTransportWithUiConfirmation } from '@dynamic-labs/viem-utils';
import { parseEvmNetworks, getOrMapViemChain, DynamicError } from '@dynamic-labs/utils';
import { ProviderChain } from '@dynamic-labs/rpc-providers';
import { MagicWalletConnectorBase } from '../../MagicWalletConnectorBase.js';
import { logger } from '../../utils/logger/logger.js';
import { EvmMagicClient } from './EvmMagicClient.js';

class MagicEvmWalletConnector extends MagicWalletConnectorBase {
    constructor(opts) {
        var _a;
        super(opts);
        // TODO: should this actually be false?
        // see: https://linear.app/dynamic-labs/issue/GVTY-252/rename-magic-based-wallets-from-using-custodialservice-to
        this.connectedChain = 'EVM';
        this.supportedChains = ['ETH', 'EVM'];
        this.evmNetworks = parseEvmNetworks(opts.evmNetworks);
        this.magicClient = new EvmMagicClient({
            apiKey: this.providerProps.apiKey,
            defaultChainId: this.chainId,
            evmNetworks: this.evmNetworks,
            walletConnector: this,
        });
        this.chainRpcProviders = opts.chainRpcProviders;
        (_a = this.chainRpcProviders) === null || _a === void 0 ? void 0 : _a.registerChainProviders(ProviderChain.EVM, (config) => {
            const rpcProviders = {};
            if (config === null || config === void 0 ? void 0 : config.evm) {
                rpcProviders.evm = parseEvmNetworks(config.evm).map((network) => {
                    var _a;
                    const rpcUrl = ((_a = network.privateCustomerRpcUrls) === null || _a === void 0 ? void 0 : _a[0]) || network.rpcUrls[0];
                    const provider = createPublicClient({
                        chain: getOrMapViemChain(network),
                        transport: http(rpcUrl),
                    });
                    return {
                        chainId: network.chainId,
                        chainName: network.name,
                        provider,
                    };
                });
            }
            return rpcProviders.evm;
        });
    }
    get chainId() {
        return this.providerProps.chainId
            ? parseInt(this.providerProps.chainId)
            : this.providerProps.defaultChainId;
    }
    filter() {
        const result = super.filter() && this.chain === 'EVM';
        if (!result)
            logger.warn(`MagicEvmWalletConnector: Magic is configured for a different chain: ${this.chain}. This wallet connector will be disabled.`);
        return result;
    }
    getDeepLink() {
        return undefined;
    }
    getBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const isLoggedIn = yield this.isLoggedIn();
            if (!isLoggedIn)
                return undefined;
            const provider = this.getPublicClientSync();
            if (!provider)
                throw this.generateDynamicErrorOnMissingProvider();
            const address = yield this.fetchPublicAddress();
            if (!address)
                return;
            return formatEther(yield provider.getBalance({ address }));
        });
    }
    getNetworkSync() {
        return this.magicClient.networkId;
    }
    getNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getNetworkSync();
        });
    }
    getSignerSync(account) {
        const client = this.magicClient.getClient();
        const provider = this.getPublicClientSync();
        if (!provider)
            throw this.generateDynamicErrorOnMissingProvider();
        const transport = createTransportWithUiConfirmation({
            publicClient: provider,
            transport: custom(client.rpcProvider),
            walletUiUtils: this.walletUiUtils,
        });
        const walletClient = createWalletClient({
            account,
            transport,
        });
        return walletClient;
    }
    getSigner() {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = this.getPublicClientSync();
            if (!provider)
                throw this.generateDynamicErrorOnMissingProvider();
            const address = yield this.fetchPublicAddress();
            return this.getSignerSync(address);
        });
    }
    getWalletClient() {
        return this.getSignerSync();
    }
    getPublicClientSync() {
        var _a, _b;
        const networkId = this.getNetworkSync();
        if (this.evmNetworks.length === 0) {
            return undefined;
        }
        const configurations = {
            cosmos: [],
            evm: this.evmNetworks,
            solana: [],
            starknet: undefined,
        };
        return (_b = (_a = this.chainRpcProviders) === null || _a === void 0 ? void 0 : _a.getEvmProviderByChainId(configurations, networkId)) === null || _b === void 0 ? void 0 : _b.provider;
    }
    generateDynamicErrorOnMissingProvider() {
        return new DynamicError(`No provider found for the current configured magic network.
      magic configured network_id: ${this.getNetworkSync()}, configured evmNetworks: ${this.evmNetworks} `);
    }
    getPublicClient() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getPublicClientSync();
        });
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            const client = this.getSignerSync();
            const account = yield this.fetchPublicAddress();
            if (!account || !client)
                return;
            return client.signMessage({
                account,
                message: messageToSign,
            });
        });
    }
    supportsNetworkSwitching() {
        return true;
    }
    switchNetwork({ networkChainId, }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!networkChainId) {
                return;
            }
            this.magicClient.selectNetwork(networkChainId);
        });
    }
}

export { MagicEvmWalletConnector };
